<ui-section class="access-management-section">
  <ui-block width="fill">
    <main class="access-management" aria-labelledby="access-management-title">
      <header class="access-management__header">
        <div>
          <h2
            id="access-management-title"
            data-testid="access-management-heading"
            [appTerminologyIdentity]="usage.heading"
          >{{ text.heading() }}</h2>
          <p [appTerminologyIdentity]="usage.description">{{ text.description() }}</p>
        </div>
        <span class="save-policy">
          <span class="save-policy__indicator" aria-hidden="true"></span>
          <span [appTerminologyIdentity]="usage.savePolicy">{{ text.savePolicy() }}</span>
        </span>
      </header>

      <div class="message-box success access-feedback" *ngIf="successMessage()" role="status" aria-live="polite">
        {{ successMessage() }}
      </div>

      <div class="message-box error access-feedback" *ngIf="errorState() as error" role="alert" aria-live="assertive">
        <p>{{ error.title }}</p>
        <ul>
          <li *ngFor="let message of error.messages">{{ message }}</li>
        </ul>
        <div class="error-meta" *ngIf="error.problemCode || error.issueCodes.length > 0 || error.correlationId">
          <span *ngIf="error.problemCode"><span [appTerminologyIdentity]="usage.problemCodeLabel">{{ text.problemCodeLabel() }}</span>: {{ error.problemCode }}</span>
          <span *ngIf="error.issueCodes.length > 0"><span [appTerminologyIdentity]="usage.issueCodesLabel">{{ text.issueCodesLabel() }}</span>: {{ error.issueCodes.join(', ') }}</span>
          <span *ngIf="error.correlationId"><span [appTerminologyIdentity]="usage.correlationIdLabel">{{ text.correlationIdLabel() }}</span>: {{ error.correlationId }}</span>
        </div>
      </div>

      <div class="access-workspace">
        <section class="workspace-panel user-panel" aria-labelledby="users-title">
          <header class="workspace-panel__header">
            <div>
              <h3 id="users-title" [appTerminologyIdentity]="usage.usersHeading">{{ text.usersHeading() }}</h3>
              <span>{{ visibleUserCount() }} <span [appTerminologyIdentity]="usage.usersShownSuffix">{{ text.usersShownSuffix() }}</span></span>
            </div>
            <label class="compact-search compact-search--users">
              <span class="visually-hidden" [appTerminologyIdentity]="usage.usersSearchLabel">{{ text.usersSearchLabel() }}</span>
              <span class="compact-search__icon" aria-hidden="true"></span>
              <span
                *ngIf="!userSearch()"
                class="compact-search__hint"
                aria-hidden="true"
                [appTerminologyIdentity]="usage.usersSearchHint"
              >{{ text.usersSearchHint() }}</span>
              <input
                type="search"
                [value]="userSearch()"
                (input)="onUserSearch($event)"
              />
            </label>
            <button
              *ngIf="canAuthor()"
              #createUserButton
              type="button"
              class="profile-button profile-button--primary user-create-open"
              data-testid="user-create-open"
              [disabled]="isCreatingUser()"
              [appTerminologyIdentity]="usage.createUserHeading"
              (click)="startCreateUser()"
            >{{ text.createUserHeading() }}</button>
          </header>

          <div class="user-grid" [attr.aria-busy]="isAssignmentsLoading()">
            <ui-grid
              width="fill"
              [dsConfig]="userCatalogGridConfig()"
              [agGridOptions]="userCatalogGridOptions"
              [showPanelHeader]="false"
              [reloadOnChangeTrigger]="userCatalogReloadTrigger()"
            ></ui-grid>
          </div>
        </section>

        <!--
          The detail panel shows one of three things: the new user form while a user is being
          created, the selected user for editing, or the "Select a user" state. It can take the
          focus itself so the focus has somewhere to land once the new user form has saved.
        -->
        <section
          #detailPanel
          class="workspace-panel detail-panel"
          data-testid="access-detail-panel"
          tabindex="-1"
          aria-labelledby="user-access-title"
        >
          <phms-platform-access-user-create-panel
            *ngIf="isCreatingUser(); else userDetail"
            class="detail-panel__create"
            [roles]="roleCatalog()"
            (created)="onUserCreated($event)"
            (cancelled)="cancelCreateUser()"
          ></phms-platform-access-user-create-panel>

          <ng-template #userDetail>
            <ng-container *ngIf="assignments() as selectedUser; else noUserSelected">
              <header class="selected-user-header">
                <span class="selected-user-header__avatar" aria-hidden="true">
                  {{ getUserInitials(selectedUser.displayName) }}
                </span>
                <div class="selected-user-header__identity">
                  <h3 id="user-access-title">{{ selectedUser.displayName }}</h3>
                  <span *ngIf="selectedUser.email; else noEmail">{{ selectedUser.email }}</span>
                  <ng-template #noEmail>
                    <span [appTerminologyIdentity]="usage.noEmailOnRecord">{{ text.noEmailOnRecord() }}</span>
                  </ng-template>
                </div>
                <span
                  class="status-label"
                  [class.status-label--inactive]="!selectedUser.isActive"
                  [appTerminologyIdentity]="selectedUser.isActive ? usage.statusActive : usage.statusInactive"
                >
                  {{ selectedUser.isActive ? text.statusActive() : text.statusInactive() }}
                </span>
              </header>

              <form
                *ngIf="userProfileDraft() as profile"
                class="user-profile-form"
                aria-labelledby="user-profile-title"
                [attr.aria-busy]="isUserProfileSaving()"
                (submit)="saveUserProfile(); $event.preventDefault()"
              >
                <header class="user-profile-form__header">
                  <h4 id="user-profile-title" [appTerminologyIdentity]="usage.profileHeading">{{ text.profileHeading() }}</h4>
                  <span><span [appTerminologyIdentity]="usage.profileLastSignIn">{{ text.profileLastSignIn() }}</span>: {{ formatLastAuthenticated(selectedUser.lastAuthenticatedUtc) }}</span>
                </header>

                <div class="user-profile-form__fields">
                  <label class="profile-field">
                    <span [appTerminologyIdentity]="usage.profileDisplayName">{{ text.profileDisplayName() }}</span>
                    <input
                      type="text"
                      required
                      maxlength="200"
                      autocomplete="name"
                      [value]="profile.displayName"
                      [disabled]="!canAuthor() || isUserProfileSaving()"
                      (input)="onUserProfileInput('displayName', $event)"
                    />
                  </label>
                  <label class="profile-field">
                    <span [appTerminologyIdentity]="usage.profileEmail">{{ text.profileEmail() }}</span>
                    <input
                      type="email"
                      maxlength="320"
                      autocomplete="email"
                      [value]="profile.email || ''"
                      [disabled]="!canAuthor() || isUserProfileSaving()"
                      (input)="onUserProfileInput('email', $event)"
                    />
                  </label>
                  <label class="profile-field">
                    <span [appTerminologyIdentity]="usage.profilePhone">{{ text.profilePhone() }}</span>
                    <input
                      type="tel"
                      maxlength="40"
                      autocomplete="tel"
                      [value]="profile.phoneNumber || ''"
                      [disabled]="!canAuthor() || isUserProfileSaving()"
                      (input)="onUserProfileInput('phoneNumber', $event)"
                    />
                  </label>
                  <label class="profile-field">
                    <span [appTerminologyIdentity]="usage.profileJobTitle">{{ text.profileJobTitle() }}</span>
                    <input
                      type="text"
                      maxlength="150"
                      autocomplete="organization-title"
                      [value]="profile.jobTitle || ''"
                      [disabled]="!canAuthor() || isUserProfileSaving()"
                      (input)="onUserProfileInput('jobTitle', $event)"
                    />
                  </label>
                  <label class="profile-field">
                    <span [appTerminologyIdentity]="usage.profileDepartment">{{ text.profileDepartment() }}</span>
                    <input
                      type="text"
                      maxlength="150"
                      autocomplete="organization"
                      [value]="profile.department || ''"
                      [disabled]="!canAuthor() || isUserProfileSaving()"
                      (input)="onUserProfileInput('department', $event)"
                    />
                  </label>
                  <div class="profile-actions">
                    <label class="profile-active-toggle">
                      <input
                        type="checkbox"
                        [checked]="profile.isActive"
                        [disabled]="!canAuthor() || isUserProfileSaving()"
                        (change)="onUserProfileActiveChange($event)"
                      />
                      <span [appTerminologyIdentity]="usage.profileActiveUser">{{ text.profileActiveUser() }}</span>
                    </label>
                    <div class="profile-actions__buttons">
                      <button
                        type="button"
                        class="profile-button profile-button--secondary"
                        [disabled]="!hasUserProfileChanges() || isUserProfileSaving()"
                        [appTerminologyIdentity]="usage.profileResetAction"
                        (click)="resetUserProfile()"
                      >{{ text.profileResetAction() }}</button>
                      <button
                        type="submit"
                        class="profile-button profile-button--primary"
                        [disabled]="!canAuthor() || !hasUserProfileChanges() || isUserProfileSaving()"
                        [appTerminologyIdentity]="isUserProfileSaving() ? usage.savingState : usage.profileSaveAction"
                      >{{ isUserProfileSaving() ? text.savingState() : text.profileSaveAction() }}</button>
                    </div>
                  </div>
                </div>
              </form>

              <div class="assignment-heading">
                <div>
                  <h4 [appTerminologyIdentity]="usage.assignmentsHeading">{{ text.assignmentsHeading() }}</h4>
                  <p [appTerminologyIdentity]="usage.assignmentsDescription">{{ text.assignmentsDescription() }}</p>
                </div>
                <span
                  *ngIf="isAssignmentSaving()"
                  class="saving-state"
                  role="status"
                  aria-live="polite"
                  [appTerminologyIdentity]="usage.savingState"
                >{{ text.savingState() }}</span>
              </div>

              <div class="assignment-tabs" role="tablist" [attr.aria-label]="text.assignmentsTablistLabel()">
                <button
                  type="button"
                  role="tab"
                  id="roles-tab"
                  aria-controls="roles-panel"
                  [attr.aria-selected]="activeAssignmentTab() === 'roles'"
                  [class.assignment-tabs__button--active]="activeAssignmentTab() === 'roles'"
                  class="assignment-tabs__button"
                  (click)="selectAssignmentTab('roles')"
                >
                  <span [appTerminologyIdentity]="usage.tabRoles">{{ text.tabRoles() }}</span>{{ ' ' }}
                  <span>{{ assignedRoleCount() }}/{{ roleCatalog().length }}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  id="permissions-tab"
                  aria-controls="permissions-panel"
                  data-testid="permissions-tab"
                  [attr.aria-selected]="activeAssignmentTab() === 'permissions'"
                  [class.assignment-tabs__button--active]="activeAssignmentTab() === 'permissions'"
                  class="assignment-tabs__button"
                  (click)="selectAssignmentTab('permissions')"
                >
                  <span [appTerminologyIdentity]="usage.tabPermissions">{{ text.tabPermissions() }}</span>{{ ' ' }}
                  <span>{{ effectivePermissionCountLabel() }}/{{ permissionCatalog().length }}</span>
                </button>
              </div>

              <section
                *ngIf="activeAssignmentTab() === 'roles'"
                id="roles-panel"
                class="assignment-tab-panel"
                role="tabpanel"
                aria-labelledby="roles-tab"
                [attr.aria-busy]="isAssignmentSaving()"
              >
                <header class="assignment-tab-panel__toolbar">
                  <label class="compact-search">
                    <span class="visually-hidden" [appTerminologyIdentity]="usage.rolesFilter">{{ text.rolesFilter() }}</span>
                    <span class="compact-search__icon" aria-hidden="true"></span>
                    <span
                      *ngIf="!roleFilter()"
                      class="compact-search__hint"
                      aria-hidden="true"
                      [appTerminologyIdentity]="usage.rolesFilter"
                    >{{ text.rolesFilter() }}</span>
                    <input type="search" [value]="roleFilter()" (input)="onRoleFilter($event)" />
                  </label>
                  <span>{{ assignedRoleCount() }} <span [appTerminologyIdentity]="usage.assignedOf">{{ text.assignedOf() }}</span> {{ roleCatalog().length }} <span [appTerminologyIdentity]="usage.assignedSuffix">{{ text.assignedSuffix() }}</span></span>
                </header>

                <div class="assignment-checkbox-grid" role="group" [attr.aria-label]="text.rolesGroupLabel()">
                  <div
                    class="assignment-option"
                    *ngFor="let role of filteredRoles(); trackBy: trackByCode"
                    [class.assignment-option--busy]="isRoleBusy(role.code)"
                    [class.assignment-option--checked]="isRoleAssigned(role.code)"
                  >
                    <input
                      [id]="'role-option-' + role.id"
                      type="checkbox"
                      [checked]="isRoleAssigned(role.code)"
                      [disabled]="isRoleToggleDisabled(role)"
                      (change)="toggleRole(role, $any($event.target).checked, $any($event.target))"
                    />
                    <label [for]="'role-option-' + role.id">{{ role.name }}</label>
                    <button
                      type="button"
                      class="assignment-info-button"
                      [pTooltip]="getRoleTooltip(role)"
                      tooltipPosition="top"
                      tooltipStyleClass="access-assignment-tooltip"
                      [showDelay]="250"
                      [appendTo]="'body'"
                      [attr.aria-label]="text.detailsFor() + ' ' + role.name"
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <circle cx="8" cy="8" r="6.25"></circle>
                        <path d="M8 7.1v4M8 4.7h.01"></path>
                      </svg>
                    </button>
                    <span
                      class="assignment-option__state"
                      *ngIf="isRoleBusy(role.code)"
                      [appTerminologyIdentity]="usage.savingState"
                    >{{ text.savingState() }}</span>
                  </div>
                  <p
                    class="assignment-checkbox-grid__empty"
                    *ngIf="filteredRoles().length === 0"
                    [appTerminologyIdentity]="usage.rolesEmpty"
                  >{{ text.rolesEmpty() }}</p>
                </div>
              </section>

              <!--
                The permissions view stays mounted for the selected user (hidden while the Roles tab is
                active) so its granted count is on the tab as soon as the user is selected and it
                re-evaluates after every role or ad hoc grant change. Its checkboxes toggle ad hoc grants
                through this page's togglePermission.
              -->
              <section
                id="permissions-panel"
                class="assignment-tab-panel assignment-tab-panel--permissions"
                role="tabpanel"
                aria-labelledby="permissions-tab"
                [hidden]="activeAssignmentTab() !== 'permissions'"
                [attr.aria-busy]="isAssignmentSaving()"
              >
                <phms-platform-access-user-effective-permissions
                  [userId]="selectedUser.userId"
                  [displayName]="selectedUser.displayName"
                  [isActive]="selectedUser.isActive"
                  [reloadTrigger]="assignmentsVersion()"
                  [canGrant]="canAuthor()"
                  [canRevoke]="canRollback()"
                  [busy]="isAssignmentsLoading() || isAssignmentSaving()"
                  (loaded)="onEffectivePermissionsLoaded($event)"
                  (toggle)="onPermissionToggle($event)"
                ></phms-platform-access-user-effective-permissions>
              </section>
            </ng-container>
          </ng-template>

          <ng-template #noUserSelected>
            <div class="detail-empty" [class.detail-empty--loading]="isAssignmentsLoading()">
              <span class="detail-empty__icon" aria-hidden="true"></span>
              <h3
                id="user-access-title"
                [appTerminologyIdentity]="isAssignmentsLoading() ? usage.detailLoadingTitle : usage.selectUserTitle"
              >{{ isAssignmentsLoading() ? text.detailLoadingTitle() : text.selectUserTitle() }}</h3>
              <p
                [appTerminologyIdentity]="isAssignmentsLoading() ? usage.detailLoadingDescription : usage.selectUserDescription"
              >{{ isAssignmentsLoading() ? text.detailLoadingDescription() : text.selectUserDescription() }}</p>
            </div>
          </ng-template>
        </section>
      </div>
    </main>
  </ui-block>
</ui-section>
